import { useEffect, useState } from "react";
import {
  FileDown, Sparkles, History, Loader2, RefreshCw,
  ChevronDown, ChevronUp, Receipt, CalendarRange, Mail, Download, Send,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { toast } from "sonner";
import api from "../../lib/api";

const SOURCE_META = {
  manual:         { label: "Mensual",     icon: FileDown,     cls: "bg-muted text-foreground border-border" },
  manual_quarter: { label: "Trimestral",  icon: Receipt,      cls: "bg-blue-100 text-blue-700 border-blue-200" },
  manual_annual:  { label: "Anual",       icon: CalendarRange, cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  scheduler:      { label: "Auto · mes",  icon: Sparkles,     cls: "bg-indigo-100 text-indigo-700 border-indigo-200" },
};

function sourceMeta(src) {
  return SOURCE_META[src] || SOURCE_META.manual;
}

function formatDateTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-ES", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function formatRelative(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "hace un momento";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  if (diff < 86400 * 30) return `hace ${Math.floor(diff / 86400)} d`;
  return "";
}

export default function FinanceExportHistoryCard({ restaurantId }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(null);
  const [downloadingKey, setDownloadingKey] = useState(null);
  const [resendingKey, setResendingKey] = useState(null);
  const [accountantEmail, setAccountantEmail] = useState("");

  const load = async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const r = await api.listFinanceExportLog(restaurantId, 50);
      setItems(r.items || []);
      setCount(r.count ?? (r.items || []).length);
    } catch {
      setItems([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Regenerate the ZIP for an existing log entry, reusing the right endpoint
   * based on the stored `source`. Gives the owner a local backup copy when
   * the accountant loses the email or needs it for an inspection.
   */
  const downloadCopy = async (entry) => {
    const key = `${entry.month_key}-${entry.sent_at}`;
    setDownloadingKey(key);
    try {
      let res;
      if (entry.source === "manual_annual") {
        const y = parseInt(entry.month_key, 10);
        res = await api.downloadFinanceAnnualExport(restaurantId, y);
      } else if (entry.source === "manual_quarter") {
        const match = (entry.month_key || "").match(/^(\d{4})-Q([1-4])$/i);
        if (!match) throw new Error("Trimestre inválido");
        res = await api.downloadFinanceQuarterExport(
          restaurantId, parseInt(match[1], 10), parseInt(match[2], 10),
        );
      } else {
        // manual | scheduler — month_key = "YYYY-MM"
        res = await api.downloadFinanceExport(restaurantId, entry.month_key);
      }
      const { blob, filename } = res;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Copia de ${entry.month_key} descargada`);
    } catch (e) {
      toast.error(e.message || "No se pudo regenerar la copia");
    } finally {
      setDownloadingKey(null);
    }
  };

  // Lazy load: only fetch when the owner expands the panel, so closed state
  // is cheap for the overview.
  useEffect(() => {
    if (open && items.length === 0 && !loading) {
      load();
      api.getAccountantEmail(restaurantId)
        .then(r => setAccountantEmail(r?.accountant_email || ""))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /**
   * Resend an existing log entry to the accountant email saved in settings.
   * Uses the same email endpoint that created the original entry — the backend
   * writes a new row in `finance_export_log`, so the history grows with a full
   * trail of who/when forwarded each filing.
   */
  const resendEmail = async (entry) => {
    const to = (accountantEmail || "").trim();
    if (!to) {
      toast.error("Configura primero el email del contable en Ajustes");
      return;
    }
    const key = `${entry.month_key}-${entry.sent_at}`;
    setResendingKey(key);
    try {
      let r;
      if (entry.source === "manual_annual") {
        const y = parseInt(entry.month_key, 10);
        r = await api.sendFinanceAnnualExportEmail(restaurantId, y, to);
      } else if (entry.source === "manual_quarter") {
        const match = (entry.month_key || "").match(/^(\d{4})-Q([1-4])$/i);
        if (!match) throw new Error("Trimestre inválido");
        r = await api.sendFinanceQuarterExportEmail(
          restaurantId, parseInt(match[1], 10), parseInt(match[2], 10), to,
        );
      } else {
        r = await api.sendFinanceExportEmail(restaurantId, entry.month_key, to);
      }
      toast.success(`${entry.month_key} reenviado a ${r.sent_to || to}`);
      // Refresh the list so the new entry shows up immediately.
      load();
    } catch (e) {
      toast.error(e.message || "No se pudo reenviar");
    } finally {
      setResendingKey(null);
    }
  };

  return (
    <Card className="border-none shadow-soft" data-testid="finance-export-history-card">
      <CardHeader className="pb-2">
        <button
          type="button"
          className="flex items-center justify-between w-full text-left"
          onClick={() => setOpen(v => !v)}
          data-testid="finance-export-history-toggle"
        >
          <div className="flex items-center gap-2">
            <History className="h-4 w-4 text-primary" />
            <CardTitle className="text-base">
              Historial de presentaciones fiscales
            </CardTitle>
            {count !== null && count > 0 && (
              <span
                className="inline-flex items-center justify-center text-[10px] font-semibold min-w-[20px] h-[20px] rounded-full bg-accent text-foreground px-1.5"
                data-testid="finance-export-history-count"
              >
                {count}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {open && (
              <span
                role="button"
                tabIndex={0}
                className="inline-flex items-center justify-center h-7 w-7 rounded hover:bg-muted cursor-pointer"
                onClick={(e) => { e.stopPropagation(); load(); }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault(); e.stopPropagation(); load();
                  }
                }}
                data-testid="finance-export-history-refresh"
                aria-label="Recargar histórico"
              >
                {loading
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <RefreshCw className="h-3.5 w-3.5" />}
              </span>
            )}
            {open
              ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
              : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
          </div>
        </button>
      </CardHeader>
      {open && (
        <CardContent className="pt-0">
          {loading && items.length === 0 ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <div className="py-6 text-center text-xs text-muted-foreground">
              Aún no se ha enviado ninguna exportación al contable. Cuando envíes
              la primera, aparecerá aquí como prueba de diligencia fiscal.
            </div>
          ) : (
            <ul className="divide-y divide-border -mx-2" data-testid="finance-export-history-list">
              {items.map((it, idx) => {
                const meta = sourceMeta(it.source);
                const Icon = meta.icon;
                const rowKey = `${it.month_key}-${it.sent_at}`;
                const isDownloading = downloadingKey === rowKey;
                return (
                  <li
                    key={`${rowKey}-${idx}`}
                    className="flex items-center gap-3 px-2 py-2.5 hover:bg-muted/40 rounded"
                    data-testid={`finance-export-history-row-${it.month_key}`}
                  >
                    <span className={`inline-flex items-center justify-center h-8 w-8 rounded-lg border shrink-0 ${meta.cls}`}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold">
                          {it.month_key}
                        </span>
                        <span className={`inline-flex items-center text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${meta.cls}`}>
                          {meta.label}
                        </span>
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate flex items-center gap-1 mt-0.5">
                        <Mail className="h-3 w-3 shrink-0" />
                        <span className="truncate">{it.sent_to || "—"}</span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[11px] font-medium">{formatDateTime(it.sent_at)}</div>
                      <div className="text-[10px] text-muted-foreground">{formatRelative(it.sent_at)}</div>
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                      <button
                        type="button"
                        className="inline-flex items-center justify-center h-8 w-8 rounded hover:bg-muted disabled:opacity-50"
                        onClick={(e) => { e.stopPropagation(); resendEmail(it); }}
                        disabled={resendingKey === rowKey || !accountantEmail}
                        title={accountantEmail
                          ? `Reenviar a ${accountantEmail}`
                          : "Configura el email del contable en Ajustes"}
                        aria-label="Reenviar al contable"
                        data-testid={`finance-export-history-resend-${it.month_key}`}
                      >
                        {resendingKey === rowKey
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <Send className="h-3.5 w-3.5 text-muted-foreground" />}
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center justify-center h-8 w-8 rounded hover:bg-muted disabled:opacity-50"
                        onClick={(e) => { e.stopPropagation(); downloadCopy(it); }}
                        disabled={isDownloading}
                        title="Descargar copia del ZIP"
                        aria-label="Descargar copia"
                        data-testid={`finance-export-history-download-${it.month_key}`}
                      >
                        {isDownloading
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <Download className="h-3.5 w-3.5 text-muted-foreground" />}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      )}
    </Card>
  );
}
